import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Checkbox,
    List,
    ListItemButton,
    ListItemAvatar,
    Avatar,
    ListItemText,
    TextField,
    InputAdornment,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Skeleton,
    IconButton,
    Alert,
    AlertTitle,
    Tooltip,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    CardActions,
    ListSubheader
} from '@mui/material';
import {
    Search as SearchIcon,
    School as SchoolIcon,
    Payment as PaymentIcon,
    Edit as EditIcon,
    WarningAmber as WarningAmberIcon,
    InfoOutlined as InfoOutlinedIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
    Clear as ClearIcon,
    PlaylistAddCheck as PlaylistAddCheckIcon,
    ErrorOutline as ErrorOutlineIcon,
    CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
    CheckBox as CheckBoxIcon,
    IndeterminateCheckBox as IndeterminateCheckBoxIcon,
    CheckCircle as CheckCircleIcon // Icon for row indicator
} from '@mui/icons-material';
import axiosInstance from '../../api/axiosConfig';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useConfirm } from '../../hooks/useConfirm';
import { styled, alpha } from '@mui/material/styles';

const getChipColor = (index, theme) => {
    const colors = [
        theme.palette.info.lighter,
        theme.palette.success.lighter,
        theme.palette.warning.lighter,
        theme.palette.secondary.lighter,
        theme.palette.primary.lighter,
        '#e0f7fa', '#fce4ec', '#fffde7', '#f1f8e9', '#ede7f6',
        '#e3f2fd', '#fff3e0',
    ];
    return colors[index % colors.length];
};

const PageContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0.4),
    minHeight: '100%',
    background: 'transparent',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.2),
    },
}));

const ContentPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2.1),
    background: `linear-gradient(180deg, ${alpha('#FFFFFF', 0.98)} 0%, ${alpha('#FFF7ED', 0.42)} 100%)`,
    borderRadius: 28,
    border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
    boxShadow: '0 18px 36px rgba(15, 23, 42, 0.05)',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.4),
    },
}));

const HeaderBox = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.3fr) minmax(260px, 0.7fr)',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
    },
}));

const HeroPanel = styled(Box)(({ theme }) => ({
    borderRadius: 6,
    padding: theme.spacing(2),
    background: `linear-gradient(135deg, ${alpha('#FFF7ED', 0.96)} 0%, ${alpha('#FFFFFF', 1)} 100%)`,
    border: `1px solid ${alpha(theme.palette.warning.main, 0.18)}`,
}));

const SummaryTile = styled(Box)(({ theme }) => ({
    borderRadius: 6,
    padding: theme.spacing(1.8),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
}));

const ListContainer = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: 6,
    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.04)',
    overflowX: 'auto',
    backgroundColor: theme.palette.background.paper,
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
    backgroundColor: alpha(theme.palette.text.primary, 0.04),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
}));

const StyledTableCell = styled(TableCell)(({ theme, isCheckCell }) => ({
    fontWeight: 700,
    color: theme.palette.text.primary,
    padding: theme.spacing(0.9, isCheckCell ? 1 : 1.2), // Less padding for check cell
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
    whiteSpace: 'nowrap',
    fontSize: '0.78rem',
    letterSpacing: '0.02em',
    textTransform: 'none',
    textAlign: isCheckCell ? 'center' : 'left',
    '&:last-child': { borderRight: 'none' },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:hover': {
        backgroundColor: alpha(theme.palette.text.primary, 0.03),
    },
    '&:last-child td, &:last-child th': {
        borderBottom: 0,
    },
    transition: 'background-color 0.15s ease-in-out',
}));

const DataTableCell = styled(TableCell)(({ theme, isCheckCell }) => ({
    padding: theme.spacing(0.85, isCheckCell ? 1 : 1.2),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
    fontSize: '0.85rem',
    verticalAlign: 'middle',
    textAlign: isCheckCell ? 'center' : 'left',
    '&:last-child': { borderRight: 'none' },
}));

const MobileCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    borderRadius: 6,
    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.04)',
    '&:last-child': {
        marginBottom: 0,
    },
    position: 'relative', // Needed for potential absolute positioning of checkmark if desired
}));

const MobileCardContent = styled(CardContent)(({ theme }) => ({
    padding: theme.spacing(2),
    '&:last-child': {
       paddingBottom: theme.spacing(2),
    },
}));

const MobileDataRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
    '&:last-child': {
        borderBottom: 'none',
    },
}));

const MobileDataLabel = styled(Typography)(({ theme }) => ({
    fontWeight: 500,
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    marginRight: theme.spacing(1),
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
}));

const MobileDataValue = styled(Box)(({ theme }) => ({
    textAlign: 'right',
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
    flex: '1 1 auto',
    minWidth: 0,
}));

const MobileCardActionsStyled = styled(CardActions)(({ theme }) => ({
    justifyContent: 'flex-end',
    padding: theme.spacing(0.8, 1.2),
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    backgroundColor: 'transparent',
}));

const ManageFeesButton = styled(Button)(({ theme }) => ({
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(0.6, 1.4),
    borderRadius: 6,
    fontSize: '0.85rem',
    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    backgroundColor: theme.palette.background.paper,
    transition: 'background-color 0.2s, border-color 0.2s',
    '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.06) },
}));

const FeeTypeChip = styled(Chip)(({ theme, chipcolor }) => ({
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
    fontWeight: 600,
    margin: theme.spacing(0.35),
    height: 'auto',
    padding: theme.spacing(0.2, 0),
    fontSize: '0.78rem',
    borderRadius: 0,
    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    transition: 'opacity 0.2s',
    '& .MuiChip-label': {
        padding: theme.spacing(0, 1),
        display: 'block',
        whiteSpace: 'normal',
        lineHeight: 1.4,
    },
    '& .MuiChip-deleteIcon': {
        color: theme.palette.text.secondary,
        '&:hover': { color: theme.palette.text.primary },
        fontSize: '1.2rem',
        margin: theme.spacing(0, 0.5, 0, -0.25),
        transition: 'color 0.2s',
    },
    '&.Mui-disabled': {
        opacity: 0.6,
        pointerEvents: 'none',
         backgroundColor: theme.palette.action.disabledBackground,
    }
}));

const ChipContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    alignItems: 'center',
}));

const NoItemsBox = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    padding: theme.spacing(3, 2),
    borderRadius: 0,
    backgroundColor: 'transparent',
    border: `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    '& .MuiSvgIcon-root': { fontSize: 48, color: theme.palette.grey[400] },
    '& .MuiTypography-h6': { color: theme.palette.text.primary, fontWeight: 500 },
    '& .MuiTypography-body1': { color: theme.palette.text.secondary, maxWidth: '80%' }
}));

const DialogStyled = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: 6,
        maxWidth: '650px', // Increased width
        width: 'calc(100% - 32px)', // Max width with margin
        boxShadow: 'none',
        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
        [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(1),
            width: `calc(100% - ${theme.spacing(2)})`,
            maxWidth: `calc(100% - ${theme.spacing(2)})`, // Ensure it fits on small screens
            maxHeight: `calc(100% - ${theme.spacing(2)})`,
        },
    },
}));

const DialogTitleStyled = styled(DialogTitle)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    padding: theme.spacing(1.2, 1.6),
    fontWeight: 700,
    fontSize: '1.1rem',
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    flexShrink: 0,
}));

const DialogContentStyled = styled(DialogContent)(({ theme }) => ({
    padding: 0,
    backgroundColor: theme.palette.background.paper,
    flexGrow: 1,
    overflowY: 'auto',
}));

const DialogActionsStyled = styled(DialogActions)(({ theme }) => ({
    padding: theme.spacing(1, 1.6),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    justifyContent: 'space-between',
    flexShrink: 0,
}));

const SearchBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0.9, 1.6),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    flexShrink: 0,
}));

const SearchTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 6,
        backgroundColor: theme.palette.background.paper,
        fontSize: '0.9rem',
        '& fieldset': { borderColor: alpha(theme.palette.divider, 0.8) },
        '&:hover fieldset': { borderColor: alpha(theme.palette.divider, 0.8) },
        '&.Mui-focused fieldset': { borderColor: theme.palette.text.primary, borderWidth: '1px' },
        '& .MuiInputBase-input': { padding: theme.spacing(1, 1.2) },
        '& .MuiIconButton-root': { color: theme.palette.text.secondary },
    },
}));

const FeeTypeListItem = styled(ListItemButton)(({ theme }) => ({
    padding: theme.spacing(1, 3),
    transition: 'background-color 0.15s ease-in-out',
    '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.6) },
    '&.Mui-selected': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) }
    },
    '& .MuiListItemAvatar-root': { minWidth: 'auto', marginRight: theme.spacing(2) },
    '& .MuiCheckbox-root': { padding: theme.spacing(0.5), marginLeft: theme.spacing(1) },
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' }
}));

const FeeTypeNameTypography = styled(Typography)(({ theme }) => ({
    fontWeight: 500,
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
}));

const FeeAmountTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
}));

const FeeTypeAvatar = styled(Avatar)(({ theme }) => ({
    background: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    width: theme.spacing(5),
    height: theme.spacing(5),
    fontSize: '1.1rem',
    borderRadius: theme.shape.borderRadius,
}));

const AssignButton = styled(Button)(({ theme }) => ({
    fontWeight: 600,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
}));

const SectionHeader = styled(ListSubheader)(({ theme }) => ({
    backgroundColor: alpha(theme.palette.grey[100], 0.9),
    backdropFilter: 'blur(3px)',
    padding: theme.spacing(0.75, 3),
    lineHeight: 'normal',
    minHeight: theme.spacing(5),
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginLeft: theme.spacing(-3),
    marginRight: theme.spacing(-3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
}));

const SectionHeaderTitle = styled(Typography)({
    fontWeight: 'inherit',
    fontSize: 'inherit',
    textTransform: 'inherit',
    letterSpacing: 'inherit',
    flexGrow: 1,
    marginRight: '8px',
});

const useManageFeesData = (schoolId) => {
    const [classes, setClasses] = useState([]);
    const [feeTypes, setFeeTypes] = useState([]);
    const [assignedFeesMap, setAssignedFeesMap] = useState(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!schoolId) {
            setError("School ID is missing or invalid.");
            setLoading(false); setClasses([]); setFeeTypes([]); setAssignedFeesMap(new Map()); return;
        }
        setLoading(true); setError(null);
        try {
            const cacheBuster = `_=${new Date().getTime()}`;
            const commonHeaders = { 'School-ID': schoolId };
            const [ classesResponse, feeTypesResponse, allAssignmentsResponse ] = await Promise.all([
                axiosInstance.get(`/api/classes?${cacheBuster}`, { headers: commonHeaders }),
                axiosInstance.get(`/api/fees/types/school/${schoolId}?${cacheBuster}`),
                axiosInstance.get(`/api/class-fees/school?${cacheBuster}`, { headers: commonHeaders })
            ]);
            const fetchedClasses = (classesResponse.data || []).sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999) || a.className.localeCompare(b.className));
            const fetchedFeeTypes = (feeTypesResponse.data || []).sort((a, b) => a.name.localeCompare(b.name));
            const allAssignments = allAssignmentsResponse.data || [];
            const newAssignedFeesMap = new Map();
            allAssignments.forEach(assign => {
                if (assign.classId && assign.assignedFeeTypes) {
                    newAssignedFeesMap.set(assign.classId, assign.assignedFeeTypes.sort((a, b) => a.name.localeCompare(b.name)));
                }
            });
            setClasses(fetchedClasses); setFeeTypes(fetchedFeeTypes); setAssignedFeesMap(newAssignedFeesMap);
        } catch (err) {
            console.error('Error fetching initial data:', err);
            setError(err.response?.data?.message || "Failed to load necessary data.");
            setClasses([]); setFeeTypes([]); setAssignedFeesMap(new Map());
        } finally { setLoading(false); }
    }, [schoolId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const refetchAssignments = useCallback(async () => {
        if (!schoolId) return;
        try {
            const commonHeaders = { 'School-ID': schoolId };
            const cacheBuster = `_=${new Date().getTime()}`;
            const res = await axiosInstance.get(`/api/class-fees/school?${cacheBuster}`, { headers: commonHeaders });
            const newMap = new Map();
            (res.data || []).forEach(assign => {
                if (assign.classId && assign.assignedFeeTypes) {
                    newMap.set(assign.classId, assign.assignedFeeTypes.sort((a, b) => a.name.localeCompare(b.name)));
                }
            });
            setAssignedFeesMap(newMap);
        } catch (err) { console.error("Failed to refetch assignments:", err); }
    }, [schoolId]);

     const refetchFeeTypes = useCallback(async () => {
        if (!schoolId) return;
        try {
            const cacheBuster = `_=${new Date().getTime()}`;
            const res = await axiosInstance.get(`/api/fees/types/school/${schoolId}?${cacheBuster}`);
            setFeeTypes((res.data || []).sort((a, b) => a.name.localeCompare(b.name)));
        } catch (err) { console.error("Failed to refetch fee types:", err); }
    }, [schoolId]);

    return { classes, feeTypes, assignedFeesMap, loading, error, setAssignedFeesMap, refetchData: fetchData, refetchAssignments, refetchFeeTypes };
};

const ManageFeesByClass = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const schoolId = localStorage.getItem('schoolId');

    const { classes, feeTypes, assignedFeesMap, loading, error, setAssignedFeesMap, refetchData, refetchAssignments, refetchFeeTypes } = useManageFeesData(schoolId);
    const [selectedFeeTypeIds, setSelectedFeeTypeIds] = useState(new Set());
    const [feeTypeDialogOpen, setFeeTypeDialogOpen] = useState(false);
    const [currentClassId, setCurrentClassId] = useState(null);
    const [dialogActionLoading, setDialogActionLoading] = useState(false);
    const [deletingChipIds, setDeletingChipIds] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const { showSnackbar } = useSnackbar();
    const { confirm, ConfirmDialog } = useConfirm();

    const currentClassName = useMemo(() => classes.find(c => c.classId === currentClassId)?.className || 'Selected Class', [classes, currentClassId]);

    const groupedAndFilteredFeeTypes = useMemo(() => {
        if (!feeTypes || feeTypes.length === 0) return {};
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        const filtered = !searchTerm ? feeTypes : feeTypes.filter(ft => ft.name.toLowerCase().includes(lowerCaseSearchTerm) || String(ft.fees).includes(lowerCaseSearchTerm));
        const grouped = filtered.reduce((acc, ft) => {
            const cat = ft.category || 'Other Fees';
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(ft); return acc;
        }, {});
        const sortedCats = Object.keys(grouped).sort((a, b) => (a === 'Other Fees' ? 1 : b === 'Other Fees' ? -1 : a.localeCompare(b)));
        const final = {}; sortedCats.forEach(cat => { final[cat] = grouped[cat]; }); return final;
    }, [feeTypes, searchTerm]);

    const totalVisibleFeeTypes = useMemo(() => Object.values(groupedAndFilteredFeeTypes).reduce((sum, items) => sum + items.length, 0), [groupedAndFilteredFeeTypes]);
    const classesWithFeesCount = useMemo(() => classes.filter((cls) => (assignedFeesMap.get(cls.classId) || []).length > 0).length, [classes, assignedFeesMap]);
    const totalAssignments = useMemo(() => Array.from(assignedFeesMap.values()).reduce((sum, feeTypesForClass) => sum + feeTypesForClass.length, 0), [assignedFeesMap]);

    const handleOpenFeeTypeDialog = useCallback((classId) => {
        setCurrentClassId(classId);
        const assignedIds = assignedFeesMap.get(classId)?.map(ft => ft.feeTypeId) || [];
        setSelectedFeeTypeIds(new Set(assignedIds));
        setSearchTerm(''); setFeeTypeDialogOpen(true);
    }, [assignedFeesMap]);

    const handleCloseFeeTypeDialog = useCallback(() => {
        if (dialogActionLoading) return;
        setFeeTypeDialogOpen(false);
        setTimeout(() => { setCurrentClassId(null); setSelectedFeeTypeIds(new Set()); setDialogActionLoading(false); }, 300);
    }, [dialogActionLoading]);

    const handleFeeTypeToggle = useCallback((feeTypeId) => {
        setSelectedFeeTypeIds(prev => {
            const newSet = new Set(prev); newSet.has(feeTypeId) ? newSet.delete(feeTypeId) : newSet.add(feeTypeId); return newSet;
        });
    }, []);

    const handleSectionToggle = useCallback((sectionItems, shouldSelect) => {
        const itemIds = sectionItems.map(item => item.feeTypeId);
        setSelectedFeeTypeIds(prev => {
            const newSet = new Set(prev);
            itemIds.forEach(id => shouldSelect ? newSet.add(id) : newSet.delete(id));
            return newSet;
        });
    }, []);

    const handleUpdateAssignments = async () => {
        if (!currentClassId || dialogActionLoading) return;
        const initialIds = new Set(assignedFeesMap.get(currentClassId)?.map(ft => ft.feeTypeId) || []);
        const finalIds = selectedFeeTypeIds;
        const toAssign = Array.from(finalIds).filter(id => !initialIds.has(id));
        const toRemove = Array.from(initialIds).filter(id => !finalIds.has(id));
        if (toAssign.length === 0 && toRemove.length === 0) { showSnackbar('No changes made.', 'info'); handleCloseFeeTypeDialog(); return; }
        setDialogActionLoading(true); let success = 0, errors = 0;
        try {
            const ops = []; const headers = { 'School-ID': schoolId };
            if (toAssign.length > 0) ops.push(axiosInstance.post(`/api/class-fees/assign-fee-types`, toAssign, { headers, params: { classId: currentClassId } }).then(() => { success += toAssign.length; }).catch(err => { errors += toAssign.length; console.error('Assign Error:', err); throw err; }));
            toRemove.forEach(id => ops.push(axiosInstance.delete(`/api/class-fees/assignment`, { params: { classId: currentClassId, feeTypeId: id }, headers }).then(() => success++).catch(err => { errors++; console.error('Remove Error:', err); showSnackbar(err.response?.data?.message || `Failed to remove fee ID ${id}.`, 'error'); })));
            await Promise.allSettled(ops);
            if (errors === 0) showSnackbar('Assignments updated successfully.', 'success');
            else if (success > 0) showSnackbar(`Assignments partially updated (${success} success, ${errors} failed).`, 'warning');
            else showSnackbar('Failed to update assignments.', 'error');
            await refetchAssignments(); handleCloseFeeTypeDialog();
        } catch (err) { showSnackbar(err.response?.data?.message || 'Error assigning fees.', 'error'); await refetchAssignments(); }
        finally { setDialogActionLoading(false); }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleDeleteFeeAssignment = useCallback(async (classId, feeTypeId, feeTypeName) => {
         if (deletingChipIds.has(feeTypeId)) return;
        const clsName = classes.find(c => c.classId === classId)?.className || `Class ID ${classId}`;
        const conf = await confirm('Confirm Removal', <span>Remove <strong>{feeTypeName}</strong> from <strong>{clsName}</strong>?</span>, { confText: 'Remove', confirmProps: { color: 'error' } });
        if (!conf) return;
        setDeletingChipIds(prev => new Set(prev).add(feeTypeId));
        try {
            await axiosInstance.delete(`/api/class-fees/assignment`, { params: { classId, feeTypeId }, headers: { 'School-ID': schoolId } });
            setAssignedFeesMap(prev => { const map = new Map(prev); map.set(classId, (map.get(classId) || []).filter(ft => ft.feeTypeId !== feeTypeId)); return map; });
            showSnackbar(`Fee "${feeTypeName}" removed.`, 'success');
        } catch (err) { console.error('Delete Error:', err); showSnackbar(err?.response?.data?.message || `Failed to remove.`, 'error'); await refetchAssignments(); }
        finally { setDeletingChipIds(prev => { const set = new Set(prev); set.delete(feeTypeId); return set; }); }
    }, [schoolId, confirm, showSnackbar, setAssignedFeesMap, classes, refetchAssignments, deletingChipIds]);

    const handleSearchChange = (event) => setSearchTerm(event.target.value);
    const clearSearch = () => setSearchTerm('');

    const renderSkeletons = (count = 4) => (
        [...Array(count)].map((_, i) => (
            isMobile ? (
                <MobileCard key={i} variant="outlined">
                    <MobileCardContent>
                        <MobileDataRow><Skeleton variant="text" width="40%" sx={{mb: 1}}/></MobileDataRow>
                        <MobileDataRow><Skeleton variant="rectangular" height={40} width="70%" /></MobileDataRow>
                    </MobileCardContent>
                     <MobileCardActionsStyled><Skeleton variant="rounded" width={90} height={32} /></MobileCardActionsStyled>
                </MobileCard>
            ) : (
                <StyledTableRow key={i}>
                    <DataTableCell isCheckCell><Skeleton variant="circular" width={20} height={20} /></DataTableCell>
                    <DataTableCell><Skeleton variant="text" width={20} /></DataTableCell>
                    <DataTableCell><Skeleton variant="text" width="60%" /></DataTableCell>
                    <DataTableCell><Skeleton variant="rectangular" height={40} width="80%" /></DataTableCell>
                    <DataTableCell align="right"><Skeleton variant="rounded" width={90} height={32} /></DataTableCell>
                </StyledTableRow>
            )
        ))
    );

    const renderClassItem = (cls, index) => {
        const feesForClass = assignedFeesMap.get(cls.classId) || [];
        const hasFeesAssigned = feesForClass.length > 0;
        const isDeletingAnyChipInRow = feesForClass.some(ft => deletingChipIds.has(ft.feeTypeId));

        const feeChips = hasFeesAssigned ? (
            <ChipContainer>
                {feesForClass.map((feeType, chipIndex) => {
                    const isChipDeleting = deletingChipIds.has(feeType.feeTypeId);
                    return (
                        <Tooltip key={feeType.feeTypeId} title={isChipDeleting ? "Removing..." : `Remove "${feeType.name}"`} placement="top">
                            <span>
                                <FeeTypeChip size="small" chipcolor={getChipColor(chipIndex, theme)}
                                    label={<span>{feeType.name} <Typography variant="caption" sx={{ opacity: 0.8, display: 'inline' }}>(₹{Number(feeType.fees || 0).toLocaleString('en-IN')})</Typography></span>}
                                    onDelete={() => handleDeleteFeeAssignment(cls.classId, feeType.feeTypeId, feeType.name)}
                                    deleteIcon={isChipDeleting ? <CircularProgress size={16} color="inherit" sx={{ margin: '0 2px' }} /> : <ClearIcon fontSize="inherit" />}
                                    disabled={isDeletingAnyChipInRow} clickable={!isDeletingAnyChipInRow} />
                            </span>
                        </Tooltip>
                    );
                })}
            </ChipContainer>
        ) : ( <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>No fees assigned</Typography> );

        const manageButton = (
            <ManageFeesButton size="small" variant="outlined" onClick={() => handleOpenFeeTypeDialog(cls.classId)} startIcon={<EditIcon fontSize='inherit' />} disabled={isDeletingAnyChipInRow}>Manage</ManageFeesButton>
        );

        const checkMarkIndicator = hasFeesAssigned ? (
             <Tooltip title="Fees are assigned to this class">
                <CheckCircleIcon sx={{ fontSize: '1.1rem', color: theme.palette.success.main, verticalAlign: 'middle' }} />
             </Tooltip>
        ) : (
             <Tooltip title="No fees assigned yet">
                 <Box sx={{ width: '1.1rem', height: '1.1rem', display: 'inline-block' }} />
             </Tooltip>
        );


        if (isMobile) {
            return (
                <MobileCard key={cls.classId} variant="outlined">
                    <MobileCardContent>
                        <MobileDataRow>
                            <MobileDataLabel>Class:</MobileDataLabel>
                            <MobileDataValue>
                                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                    {checkMarkIndicator}
                                    <SchoolIcon fontSize="small" sx={{ color: theme.palette.action.active, ml: 0.5 }} />
                                    <Typography variant="body2" fontWeight={500}>{cls.className}</Typography>
                                </Box>
                            </MobileDataValue>
                        </MobileDataRow>
                        <MobileDataRow>
                             <MobileDataLabel>Assigned Fees:</MobileDataLabel>
                             <MobileDataValue>{feeChips}</MobileDataValue>
                        </MobileDataRow>
                    </MobileCardContent>
                    <MobileCardActionsStyled>{manageButton}</MobileCardActionsStyled>
                </MobileCard>
            );
        }

        return (
            <StyledTableRow key={cls.classId}>
                <DataTableCell isCheckCell>{checkMarkIndicator}</DataTableCell>
                <DataTableCell sx={{ width: '5%', textAlign: 'center' }}>{index + 1}</DataTableCell>
                <DataTableCell sx={{ width: '25%' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <SchoolIcon fontSize="small" sx={{ color: theme => theme.palette.action.active, mr: 0.5 }} />
                        <Typography variant="body2" fontWeight={500}>{cls.className}</Typography>
                    </Box>
                </DataTableCell>
                <DataTableCell>{feeChips}</DataTableCell>
                <DataTableCell align="right" sx={{ width: '15%' }}>{manageButton}</DataTableCell>
            </StyledTableRow>
        );
    };

    const renderContent = () => {
        if (loading) {
            return isMobile ? <ListContainer>{renderSkeletons()}</ListContainer> : (
                    <StyledTableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                        <Table size="small">
                            <StyledTableHead>
                                <TableRow>
                                    <StyledTableCell isCheckCell sx={{ width: '3%' }}><Tooltip title="Indicates if fees are assigned"><InfoOutlinedIcon fontSize="inherit" /></Tooltip></StyledTableCell>
                                    <StyledTableCell sx={{ width: '5%', textAlign: 'center' }}>#</StyledTableCell>
                                    <StyledTableCell sx={{ width: '25%' }}>Class Name</StyledTableCell>
                                    <StyledTableCell>Assigned Fee Types</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ width: '15%' }}>Actions</StyledTableCell>
                                </TableRow>
                            </StyledTableHead>
                            <TableBody>{renderSkeletons()}</TableBody>
                        </Table>
                    </StyledTableContainer>
            );
        }
        if (error) return <Alert severity="error" variant="outlined" icon={<ErrorOutlineIcon />} sx={{ mt: 3 }}><AlertTitle>Error Loading Data</AlertTitle>{error}<Button color="error" size="small" onClick={refetchData} sx={{ ml: -0.5, mt: 1 }}>Try Again</Button></Alert>;
        if (!schoolId) return <NoItemsBox><WarningAmberIcon /><Typography variant="h6">School Not Selected</Typography><Typography variant="body1">Please select a school.</Typography></NoItemsBox>;
        if (classes.length === 0) return <NoItemsBox><SchoolIcon /><Typography variant="h6">No Classes Found</Typography><Typography variant="body1">Add classes first.</Typography></NoItemsBox>;
        if (feeTypes.length === 0 && classes.length > 0) return <NoItemsBox><PaymentIcon /><Typography variant="h6">No Fee Types Defined</Typography><Typography variant="body1">Define Fee Types before assigning.</Typography><Button sx={{ mt: 1 }} onClick={refetchFeeTypes} variant='outlined' size='small'>Check Again</Button></NoItemsBox>;

        return isMobile ? <ListContainer>{classes.map((cls, index) => renderClassItem(cls, index))}</ListContainer> : (
                <StyledTableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table aria-label="manage fees per class table" size="small">
                        <StyledTableHead>
                            <TableRow>
                                <StyledTableCell isCheckCell sx={{ width: '3%' }}><Tooltip title="Indicates if fees are assigned"><InfoOutlinedIcon fontSize="inherit" /></Tooltip></StyledTableCell>
                                <StyledTableCell sx={{ width: '5%', textAlign: 'center' }}>#</StyledTableCell>
                                <StyledTableCell sx={{ width: '25%' }}>Class Name</StyledTableCell>
                                <StyledTableCell>Assigned Fee Types</StyledTableCell>
                                <StyledTableCell align="right" sx={{ width: '15%' }}>Actions</StyledTableCell>
                            </TableRow>
                        </StyledTableHead>
                        <TableBody>{classes.map((cls, index) => renderClassItem(cls, index))}</TableBody>
                    </Table>
                </StyledTableContainer>
            );
    };

    return (
        <PageContainer>
            <ContentPaper elevation={0}>
                 <HeaderBox>
                    <HeroPanel>
                        <Typography variant="overline" sx={{ color: 'warning.dark', fontWeight: 800, letterSpacing: '0.12em' }}>
                            Class Mapping
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 0.8, fontWeight: 800, color: 'text.primary' }}>
                            Attach the right fee structures to each class without confusion
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
                            This view is built to make mapping obvious: scan each class, see what is already attached, and open one focused dialog to assign or clean up fees.
                        </Typography>
                    </HeroPanel>
                    <Box display="grid" gap={1.5}>
                        <SummaryTile>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
                                Classes With Fees
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 0.8, fontWeight: 800 }}>
                                {classesWithFeesCount} / {classes.length || 0}
                            </Typography>
                        </SummaryTile>
                        <SummaryTile>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
                                Total Assignments
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 0.8, fontWeight: 800 }}>
                                {totalAssignments}
                            </Typography>
                        </SummaryTile>
                    </Box>
                </HeaderBox>
                {renderContent()}
                <DialogStyled open={feeTypeDialogOpen} onClose={handleCloseFeeTypeDialog} aria-labelledby="manage-fees-dialog-title">
                    <DialogTitleStyled id="manage-fees-dialog-title">Manage Fees for: {currentClassName}</DialogTitleStyled>
                    <SearchBox>
                         <SearchTextField placeholder="Search Fee Types..." variant="outlined" size="small" fullWidth onChange={handleSearchChange} value={searchTerm}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                                endAdornment: searchTerm ? <InputAdornment position="end"><Tooltip title="Clear search"><IconButton onClick={clearSearch} size="small" edge="end"><ClearIcon fontSize="small" /></IconButton></Tooltip></InputAdornment> : null
                            }} aria-label="Search available fee types" />
                    </SearchBox>
                    <DialogContentStyled>
                        {totalVisibleFeeTypes > 0 ? (
                            <List disablePadding sx={{ pt: 0 }}>
                                {Object.entries(groupedAndFilteredFeeTypes).map(([category, items]) => {
                                    const visibleIds = items.map(item => item.feeTypeId);
                                    const selectedCount = visibleIds.filter(id => selectedFeeTypeIds.has(id)).length;
                                    const isChecked = visibleIds.length > 0 && selectedCount === visibleIds.length;
                                    const isIndeterminate = selectedCount > 0 && selectedCount < visibleIds.length;
                                    return (
                                        <React.Fragment key={`section-${category}`}>
                                            <SectionHeader>
                                                <SectionHeaderTitle>{category} ({items.length})</SectionHeaderTitle>
                                                <Tooltip title={isChecked ? "Deselect All" : "Select All"}><span>
                                                    <Checkbox size="small" checked={isChecked} indeterminate={isIndeterminate} onChange={(e) => handleSectionToggle(items, e.target.checked)}
                                                        disabled={items.length === 0} inputProps={{ 'aria-label': `Select all ${category}` }} icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                        checkedIcon={<CheckBoxIcon fontSize="small" />} indeterminateIcon={<IndeterminateCheckBoxIcon fontSize="small" />} />
                                                </span></Tooltip>
                                            </SectionHeader>
                                            {items.map((ft) => {
                                                const selected = selectedFeeTypeIds.has(ft.feeTypeId);
                                                const labelId = `fee-label-${ft.feeTypeId}`;
                                                return (
                                                    <FeeTypeListItem key={ft.feeTypeId} selected={selected} onClick={() => handleFeeTypeToggle(ft.feeTypeId)}
                                                        secondaryAction={<Checkbox edge="end" checked={selected} onChange={() => handleFeeTypeToggle(ft.feeTypeId)} inputProps={{ 'aria-labelledby': labelId }} size="small"/>} aria-controls={labelId}>
                                                        <ListItemAvatar><FeeTypeAvatar variant="rounded"><PaymentIcon fontSize="inherit" /></FeeTypeAvatar></ListItemAvatar>
                                                        <ListItemText id={labelId} primary={<FeeTypeNameTypography noWrap>{ft.name}</FeeTypeNameTypography>}
                                                            secondary={<FeeAmountTypography>₹ {Number(ft.fees || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</FeeAmountTypography>} />
                                                    </FeeTypeListItem>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        ) : ( <Typography align="center" color="text.secondary" sx={{ p: 4, fontSize: '0.9rem' }}>{searchTerm ? 'No fee types found matching search.' : 'No Fee Types defined.'}</Typography> )}
                    </DialogContentStyled>
                    <DialogActionsStyled>
                        <Button onClick={handleCloseFeeTypeDialog} color="inherit" variant='text' size="small" disabled={dialogActionLoading}>Cancel</Button>
                        <AssignButton variant="contained" size="medium" onClick={handleUpdateAssignments} disabled={dialogActionLoading} startIcon={dialogActionLoading ? <CircularProgress size={18} color="inherit" /> : <PlaylistAddCheckIcon />}>
                            {dialogActionLoading ? 'Updating...' : `Update (${selectedFeeTypeIds.size})`}
                        </AssignButton>
                    </DialogActionsStyled>
                </DialogStyled>
                <ConfirmDialog />
            </ContentPaper>
        </PageContainer>
    );
};

export default ManageFeesByClass;
